/* eslint-disable @next/next/no-img-element */
import React, { FC, useRef } from "react";
import { getContentfulImage } from "../hooks";
import type { ContentfulImageOptions } from "../types";
import type { Asset } from "contentful";
import { useInView } from "@/hooks/reveal-in-view";

interface Props {
  src: Asset;
  options?: ContentfulImageOptions;
  fallbackSrc?: Asset;
  alt?: string;
  className?: string;
  pictureClassName?: string;
  draggable?: boolean;
  quality?: number;
  format?: string;
  width?: number;
  original?: boolean;
  noInline?: boolean;
  blurredBg?: boolean;
  style?: React.CSSProperties;
  animateInView?: boolean;
  /**
   * How this image is expected to be displayed, forwarded to the
   * srcset-driven `sizes` attribute. Defaults to `100vw`.
   */
  sizes?: string;
  loading?: "lazy" | "eager";
  /** Set to "high" for the LCP image so the browser fetches it first. */
  fetchPriority?: "high" | "low" | "auto";
}

export const Image: FC<Props> = (props) => {
  const container = useRef<HTMLImageElement | null>(null);
  const data: any = props.src.fields ? props.src.fields : props.src;
  const image = getContentfulImage(data, props.options);
  const isInView = useInView(container, { once: true, margin: "0px 0px 25% 0px" });

  const sizes = props.sizes || "100vw";

  const img = (
    <img
      ref={container}
      className={props.className}
      style={{
        ...(!props.blurredBg
          ? !props.noInline
            ? { ...props.style, width: "100%", height: "100%", objectFit: "cover" }
            : { ...props.style }
          : { ...props.style, backgroundImage: "url(" + image.src + ")" }),
        ...(props.animateInView
          ? {
              transform: isInView ? "scale(1)" : "scale(1.07)",
              transition: "all 0.5s cubic-bezier(0.17, 0.55, 0.55, 1)",
            }
          : {}),
      }}
      src={image.src}
      alt={
        "" +
        (props.alt ||
          props.src?.fields?.title ||
          props.src?.fields?.description ||
          props.src?.fields?.file?.fileName)
      }
      width={image.width}
      height={image.height}
      draggable={props.draggable ?? false}
      // Lazy by default: most Image usages are below the fold, and eager
      // images compete with the LCP resource for bandwidth. Above-fold
      // hero images opt out with loading="eager" (+ fetchPriority="high").
      loading={
        props.loading ?? (props.fetchPriority === "high" ? "eager" : "lazy")
      }
      // React 18.2 does not know the camelCase fetchPriority prop yet, but
      // passes unknown lowercase attributes straight through to the DOM.
      {...({ fetchpriority: props.fetchPriority } as any)}
    />
  );

  if (image.parsable && !props.original) {
    return (
      <picture className={props.className}>
        <source srcSet={image.webpSrcSet} sizes={sizes} type="image/webp" />
        <source
          srcSet={image.srcSet}
          sizes={sizes}
          type={"" + data.file?.contentType}
        />
        {img}
      </picture>
    );
  }

  return img;
};
